import React, { useState, useEffect } from 'react';
import CompareHeader from './compareHeader/CompareHeader';
import CompareTable from './compareTable/CompareTable';
import CompareFooter from './compareFooter/CompareFooter';
import useCompareMediaQuery, {
  SMALL,
  MEDIUM,
  LARGE,
  XLARGE,
} from './useCompareMediaQuery';
import FeatureDetailsModal from './featureDetailsModal/FeatureDetailsModal';
import Fuse from 'fuse.js';
import { get, isEqual, sortBy } from 'lodash';
import { normalizeSearchFeatures } from './compareSearch/CompareSearch';

const SORT_MAP = {
  standard: 3,
  available: 2,
  'n/a': 1,
};

const SEARCH_SCROLL_STATES = {
  DEFAULT: 0,
  OPEN_ACCORDION: 1,
  SCROLL_TO_FEATURE: 2,
  SCROLLED: 3,
};

const MIN_COL_WIDTH = 250;

const getInitialCompareList = (compareGroups) => {
  return Object.entries(compareGroups).map(([groupKey, group]) => {
    return {
      groupKey,
      group: Object.entries(group).map(([featureKey, feature]) => {
        return { featureKey, feature };
      }),
    };
  });
};

const getFeatureRowScore = (columnOptions, index, columns, groupKey, obj) => {
  if (
    (
      get(columns[index].groups, [groupKey, obj.featureKey]) || ''
    ).toLowerCase() === 'standard'
  ) {
    let sortScore = 1000;
    columnOptions.forEach(({ activeIndex }) => {
      const indexDiff = activeIndex - index;
      let scoreReduction = indexDiff ? indexDiff * 10 : 1;
      scoreReduction =
        scoreReduction < 0
          ? Math.abs(scoreReduction) + 1
          : Math.abs(scoreReduction);
      sortScore +=
        (
          get(columns[activeIndex].groups, [groupKey, obj.featureKey]) || ''
        ).toLowerCase() === 'standard'
          ? 100 / scoreReduction
          : 0;
    });
    return sortScore;
  }
  return (
    SORT_MAP[
      (
        get(columns[index].groups, [groupKey, obj.featureKey]) || ''
      ).toLowerCase()
    ] || 0
  );
};

const getSortedCompareList = (compareList, index, columns, columnOptions) => {
  return compareList.map(({ groupKey, group }) => {
    return {
      groupKey,
      group: [...group].sort((a, b) => {
        const columnValA = getFeatureRowScore(
          columnOptions,
          index,
          columns,
          groupKey,
          a
        );
        const columnValB = getFeatureRowScore(
          columnOptions,
          index,
          columns,
          groupKey,
          b
        );
        return columnValB - columnValA;
      }),
    };
  });
};

const Compare = ({
  headers,
  columns,
  compareGroups,
  isScrolled,
  isScrolledUp,
  setIsScrolledUp,
  onSelect,
  filterText,
  displayFilterCount,
  displayInTableFilter,
  driveTypeSectionEl,
  isFeatures = false,
  defaultSection,
  displaySort = true,
  hideMSRPDisclosure,
  analyticsKey,
  selectedIndex = 0,
  itemDescription = 'Trim',
  scrollToSearchFeature,
}) => {
  const [columnOptions, setColumnOptions] = useState([]);
  const [staticTable, setStaticTable] = useState(true);
  const [showViewControl, setShowViewControl] = useState(false);
  const [displayThreeCols, setDisplayThreeCols] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [checkedAnimation, setCheckedAnimation] = useState(false);
  const [featureModalData, setFeatureModalData] = useState(null);
  const [resizeWatch, setResizeWatch] = useState({
    watchResize: false,
    autoRotate: true,
    currentBreakPoint: '',
  });
  const [compareGroupList, setCompareGroupList] = useState([]);

  const [fuse, setFuse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchFeature, setSearchFeature] = useState({});
  const [searchScrollState, setSearchScrollState] = useState(
    SEARCH_SCROLL_STATES.DEFAULT
  );
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const currentBreakPoint = useCompareMediaQuery();

  // TODO: Probably worth refactoring this, I think since we are immutably updating each object on render it will rerender our useEffect
  // anytime any prop is changes coming from parent.
  const [columnValueTracker, setColumnValueTracker] = useState(null);

  const getPrev = (current) => {
    return (current - 1 + columns.length) % columns.length;
  };

  const getNext = (current) => {
    return (current + 1) % columns.length;
  };

  const getInitialIndex = (columnIndex) => {
    return (selectedIndex + columnIndex) % columns.length;
  };

  const onColumnFilterToggle = () => {
    setDisplayThreeCols(!displayThreeCols);
  };

  const onFeatureModalOpen = (featureModalInfo) => {
    setFeatureModalData({ featureModalInfo, isOpen: true });
  };

  const onFeatureModalClose = () => {
    setFeatureModalData({ isOpen: false });
  };

  // TODO: highlight columns that are sorted once we have all the styleguides for it.
  const onSort = (activeColumnIndex, columnIndex) => {
    const activeColumn = columnOptions[columnIndex];
    if (activeColumn.activeIndex === activeColumn.goalIndex) {
      setColumnOptions(
        columnOptions.map((option, i) => {
          if (i === columnIndex) {
            if (!option.isSorted) {
              setCompareGroupList(
                getSortedCompareList(
                  compareGroupList,
                  activeColumnIndex,
                  columns,
                  columnOptions
                )
              );
            } else {
              setCompareGroupList(getInitialCompareList(compareGroups));
            }
            return {
              ...option,
              isSorted: !option.isSorted,
            };
          }
          return { ...option, isSorted: false };
        })
      );
    }
  };

  useEffect(() => {
    let columnIntervalId = null;
    if (columnOptions.length > 0) {
      if (!staticTable && !checkedAnimation) {
        columnIntervalId = setInterval(() => {
          setColumnOptions(
            columnOptions.map((column, i) => {
              if (column.goalIndex !== column.activeIndex) {
                return {
                  ...column,
                  activeIndex: getNext(column.activeIndex),
                };
              }
              if (
                i === columnOptions.length - 1 &&
                column.goalIndex === column.activeIndex
              ) {
                clearInterval(columnIntervalId);
                setCheckedAnimation(true);
              }
              return column;
            })
          );
        }, 1500);
      }
    }
    return () => clearInterval(columnIntervalId);
  }, [columnOptions]);

  // Converted object to list so we can adjust feature order on sort.
  useEffect(() => {
    if (compareGroups) {
      setCompareGroupList(getInitialCompareList(compareGroups));
    }
  }, [compareGroups]);

  const setColumns = (cols, prepAnimation = false) => {
    if (prepAnimation) {
      setColumnOptions(
        cols.map((_, i) => {
          const goalIndex = getInitialIndex(i);
          let activeIndex = getPrev(goalIndex);
          if (i === cols.length - 1) {
            activeIndex = getPrev(activeIndex);
          }
          return {
            activeIndex: activeIndex,
            goalIndex: goalIndex,
            animateNext: true,
            isSorted: false,
          };
        })
      );
    } else {
      setColumnOptions(
        cols.map((_, i) => {
          const index = getInitialIndex(i);
          return {
            activeIndex: index,
            goalIndex: index,
            animateNext: true,
            isSorted: false,
          };
        })
      );
    }
  };

  useEffect(() => {
    const { watchResize, autoRotate, currentBreakPoint } = resizeWatch;
    let colWatch = null;

    const colXLWatch = (initialLoad = false) => {
      if (!initialLoad) {
        setCheckedAnimation(true);
      }
      const roomForCols =
        document.querySelector('.compare-header__carousel-container')
          .offsetWidth /
          MIN_COL_WIDTH >=
        columns.length;
      if (roomForCols) {
        if (!displayThreeCols) {
          const newCols = Array.from(Array(columns.length).keys());
          if (newCols.length !== columnOptions.length) {
            setColumns(newCols);
          }
          setShowViewControl(true);
          setStaticTable(true);
        } else {
          if (columnOptions.length !== 3) {
            setColumns(Array.from(Array(3).keys()), initialLoad);
          }
          setShowViewControl(true);
          setStaticTable(false);
        }
      } else {
        if (columnOptions.length !== 3) {
          setColumns(Array.from(Array(3).keys()), initialLoad);
        }
        setShowViewControl(false);
        setStaticTable(false);
      }
    };

    const colLWatch = (initialLoad = false) => {
      if (!initialLoad) {
        setCheckedAnimation(true);
      }
      const roomForXCols = Math.floor(
        document.querySelector('.compare-header__carousel-container')
          .offsetWidth / MIN_COL_WIDTH
      );

      const colsAvailable = roomForXCols >= 3 ? 3 : 2;

      if (columns.length > colsAvailable) {
        if (columnOptions.length !== colsAvailable) {
          setColumns(Array.from(Array(colsAvailable).keys()), initialLoad);
        }
        setStaticTable(false);
      } else {
        const newCols = Array.from(Array(columns.length).keys());
        if (columnOptions.length !== newCols.length) {
          setColumns(newCols);
        }
        setStaticTable(true);
      }
      setShowViewControl(false);
    };

    switch (currentBreakPoint) {
      case XLARGE:
        colWatch = colXLWatch;
        break;
      case LARGE:
        colWatch = colLWatch;
        break;
    }

    const resizeWatcher = () => colWatch();
    if (watchResize) {
      colWatch(autoRotate && !checkedAnimation);
      window.addEventListener('resize', resizeWatcher);
    }
    return () => {
      window.removeEventListener('resize', resizeWatcher);
    };
  }, [resizeWatch, checkedAnimation, displayThreeCols, columnOptions]);

  // small (< 600) - up to 2 col
  // md (> 600) - up to 2 col
  // lg (>960) - 2 - 3 cols
  // xl (> 1500) - 3 max rotating columns or static columns up to how many can fit
  useEffect(() => {
    let isStatic = false;
    let displayViewControls = false;
    let tableCols = [];
    let setOptions = true;

    if (
      columns &&
      currentBreakPoint &&
      selectedIndex !== -1 &&
      !isEqual(columns, columnValueTracker)
    ) {
      switch (currentBreakPoint) {
        case XLARGE:
          if (columns.length <= 3) {
            tableCols = Array.from(Array(columns.length).keys());
            isStatic = true;
          } else {
            setOptions = false;
          }
          break;
        case LARGE:
          setOptions = false;
          break;
        case MEDIUM:
        case SMALL:
          if (columns.length <= 2) {
            tableCols = Array.from(Array(columns.length).keys());
            isStatic = true;
          } else {
            tableCols = Array.from(Array(2).keys());
          }
          break;
        default:
          break;
      }
      if (setOptions) {
        setColumns(tableCols, !isStatic && initialLoad);
        setStaticTable(isStatic);
        setShowViewControl(displayViewControls);
        setResizeWatch({
          watchResize: false,
          autoRotate: false,
          currentBreakPoint,
        });
      } else {
        let resizeObj = {
          watchResize: true,
          autoRotate: false,
          currentBreakPoint,
        };
        if (initialLoad) {
          resizeObj = { ...resizeObj, autoRotate: true };
        }
        setResizeWatch(resizeObj);
      }
      if (initialLoad) {
        setInitialLoad(false);
      }
      setColumnValueTracker(columns);
    }
  }, [columns, selectedIndex, currentBreakPoint]);

  const handleIndexChange = (indexToChange, val, animateNext) => {
    setCompareGroupList(getInitialCompareList(compareGroups));
    setColumnOptions(
      columnOptions.map((columnOption, i) => {
        if (i !== indexToChange) {
          return {
            ...columnOption,
            goalIndex: columnOption.activeIndex,
            isSorted: false,
          };
        }
        return {
          ...columnOption,
          activeIndex: val,
          goalIndex: val,
          animateNext,
          isSorted: false,
        };
      })
    );
  };

  useEffect(() => {
    const normalizedSearchFeatures = normalizeSearchFeatures(compareGroups);
    const fuse = new Fuse(normalizedSearchFeatures, {
      keys: ['description'],
      useExtendedSearch: true,
      ignoreLocation: true,
      threshold: 0,
    });
    setFuse(fuse);
  }, [compareGroups]);

  const onSearchTermChange = (newValue) => {
    const suggestions = fuse.search(`^${newValue} | '" ${newValue}"`);
    const sortedSuggestions = sortBy(suggestions, [
      (suggestion) => suggestion.item.description,
    ]);
    setSearchTerm(newValue);
    setSearchSuggestions(sortedSuggestions);
  };

  const onSearchSuggestionSelection = (suggestion) => {
    onSearchTermChange(suggestion.item.description);
    setSearchFeature(suggestion.item);
    setSearchScrollState(SEARCH_SCROLL_STATES.OPEN_ACCORDION);
  };

  const renderSuggestion = (searchTerm, suggestion) => {
    const description = suggestion.item.description.replace(
      new RegExp(`(^|\\s)${searchTerm}`, 'ig'),
      '<b>$&</b>'
    );
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: description,
        }}
      />
    );
  };

  useEffect(() => {
    if (searchScrollState === SEARCH_SCROLL_STATES.SCROLLED && isScrolledUp) {
      setSearchScrollState(SEARCH_SCROLL_STATES.DEFAULT);
      setIsScrolledUp(false);
    }
  }, [searchScrollState, isScrolledUp]);

  return (
    <>
      <CompareHeader
        headers={headers}
        columnOptions={columnOptions}
        showViewControl={showViewControl}
        staticTable={staticTable}
        handleIndexChange={handleIndexChange}
        isStatic={staticTable}
        columnLimiter={3}
        limitColumns={displayThreeCols}
        onColumnFilterToggle={onColumnFilterToggle}
        onSort={onSort}
        isScrolled={isScrolled}
        isScrolledUp={isScrolledUp}
        filterText={filterText}
        displayFilterCount={displayFilterCount}
        displayInTableFilter={displayInTableFilter}
        driveTypeSectionEl={driveTypeSectionEl}
        displaySort={displaySort}
        hideMSRPDisclosure={hideMSRPDisclosure}
        analyticsKey={analyticsKey}
        itemDescription={itemDescription}
        searchTerm={searchTerm}
        searchSuggestions={searchSuggestions}
        onSearchTermChange={onSearchTermChange}
        onSearchSuggestionSelection={onSearchSuggestionSelection}
        renderSuggestion={renderSuggestion}
        isSearchFocused={isSearchFocused}
        setIsSearchFocused={setIsSearchFocused}
      />
      <CompareTable
        columns={columns}
        headers={headers}
        columnOptions={columnOptions}
        staticTable={staticTable}
        compareGroupList={compareGroupList}
        onFeatureModalOpen={onFeatureModalOpen}
        defaultSection={defaultSection}
        analyticsKey={analyticsKey}
        searchFeature={searchFeature}
        openSearchFeatureAccordion={
          searchScrollState === SEARCH_SCROLL_STATES.OPEN_ACCORDION
        }
        onOpenSearchFeatureAccordion={() => {
          setSearchScrollState(SEARCH_SCROLL_STATES.SCROLL_TO_FEATURE);
        }}
        scrollToSearchFeature={
          searchScrollState === SEARCH_SCROLL_STATES.SCROLL_TO_FEATURE &&
          scrollToSearchFeature
        }
        onScrollToSearchFeature={() => {
          setSearchScrollState(SEARCH_SCROLL_STATES.SCROLLED);
        }}
      />
      <div className="compare-footer__container">
        <CompareFooter
          columnOptions={columnOptions}
          columns={columns}
          onSelect={onSelect}
          isFeatures={isFeatures}
          analyticsKey={analyticsKey}
          headers={headers}
          itemDescription={itemDescription}
        />
      </div>
      {featureModalData && featureModalData.isOpen && (
        <FeatureDetailsModal
          onClose={onFeatureModalClose}
          featureModalData={featureModalData}
        />
      )}
    </>
  );
};

export default Compare;
